import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { aboutData } from '@/mocks/about';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(
        'https://readdy.ai/api/form/d9q3b6fnahb7382ov5ig',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(
            Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        setFormStatus('error');
        setFormError('服务器响应异常，请稍后重试');
        return;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || responseText;
        if (
          typeof serverMsg === 'string' &&
          (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))
        ) {
          setFormStatus('error');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 鲍鱼视频',
            description: aboutData.intro,
            url: 'https://www.baoyushipin.com/about',
            about: {
              '@type': 'Organization',
              name: aboutData.brandName,
              description: aboutData.tagline,
              foundingDate: '2020',
              url: 'https://www.baoyushipin.com/',
              contactPoint: {
                '@type': 'ContactPoint',
                telephone: aboutData.contact.phone,
                contactType: 'customer service',
                availableLanguage: 'Chinese',
              },
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.baoyushipin.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: '关于我们',
              },
            ],
          }),
        }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="relative rounded-2xl overflow-hidden mb-14 h-[250px] md:h-[380px]">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20abstract%20ocean%20themed%20background%20with%20flowing%20curves%2C%20elegant%20golden%20and%20amber%20gradient%2C%20suggesting%20seawater%20and%20abalone%20shell%20textures%2C%20minimalist%20luxurious%20aesthetic%2C%20soft%20warm%20lighting%2C%20abstract%20artistic%20style&width=1800&height=600&seq=about-hero&orientation=landscape"
              alt="关于鲍鱼视频"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-lg text-background-50/80 max-w-2xl">
                {aboutData.tagline}
              </p>
            </div>
          </section>

          <section className="max-w-4xl mx-auto mb-14">
            <div className="bg-background-50 rounded-xl p-6 md:p-10 border border-background-200/60">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                品牌介绍
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                {aboutData.intro}
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {aboutData.mission}
              </p>
            </div>
          </section>

          <section className="max-w-4xl mx-auto mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-6 top-2 bottom-2 w-0.5 bg-background-200"></div>
              <div className="space-y-4 md:space-y-6">
                {aboutData.milestones.map((milestone, index) => (
                  <div key={index} className="relative flex items-start gap-4 md:gap-6 pl-12 md:pl-16">
                    <div className="absolute left-3 md:left-[21px] w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 mt-1.5"></div>
                    <div className="bg-background-50 rounded-xl p-4 md:p-5 border border-background-200/60 flex-1">
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 font-bold mb-2">
                        {milestone.year}
                      </span>
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {milestone.event}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="max-w-4xl mx-auto mb-14">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {aboutData.team.map((member, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl p-5 border border-background-200/60 text-center"
                >
                  <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-secondary-100 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-secondary-600"></i>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="max-w-4xl mx-auto mb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  联系方式
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电子邮箱</p>
                      <p className="text-sm text-foreground-800 font-medium">
                        {aboutData.contact.email}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">联系电话</p>
                      <p className="text-sm text-foreground-800 font-medium">
                        {aboutData.contact.phone}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-wechat-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信</p>
                      <p className="text-sm text-foreground-800 font-medium">
                        {aboutData.contact.wechat}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">办公地址</p>
                      <p className="text-sm text-foreground-800 font-medium">
                        {aboutData.contact.address}
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm text-foreground-800 font-medium">
                        {aboutData.contact.workHours}
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-background-50 rounded-xl p-6 md:p-8 border border-background-200/60">
                <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">
                  给我们留言
                </h2>
                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <div className="home-honeypot-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <div>
                    <label htmlFor="about-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-secondary-500">*</span>
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-100 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-secondary-500">*</span>
                    </label>
                    <input
                      id="about-email"
                      type="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-100 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      id="about-subject"
                      type="text"
                      name="subject"
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-100 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="about-message" className="block text-xs font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-secondary-500">*</span>
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2.5 text-sm rounded-lg border border-background-200 bg-background-100 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line mr-2"></i>
                    发送留言
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                      <i className="ri-check-line mr-1"></i>
                      留言发送成功！我们会尽快与您联系。
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formError || '提交失败，请稍后重试'}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}